<template>
  <a-modal
    :visible="value"
    width="70%"
    :destroyOnClose="true"
    @cancel="$emit('close')"
    @ok="$emit('close')"
  >
    <template #title>
      <div class="info-modal__header">
        <div class="info-modal__title">
          <span>Custom page info</span>
          <a-switch
            v-if="data"
            size="small"
            :checked="data?.is_active"
            @change="(val) => $emit('changeActive', val, data?.uuid)"
            :disabled="!hasEditPermission"
          />
        </div>
        <span class="info-modal__uuid">UUID: {{ data?.uuid }}</span>
      </div>
    </template>
    <template #footer>
      <div class="info-modal__footer">
        <div class="info-modal__left">
          <a-button @click="clipboardHandle('Custom page', JSON.stringify(data, null, 2))">
            <a-icon type="copy" class="font-size-14" />
            <span>Copy content</span>
          </a-button>
        </div>
        <div class="info-modal__right">
          <a-button @click="$emit('openEditPage', data?.uuid)">
            <a-icon type="edit" class="font-size-14" />
            <span>Edit</span>
          </a-button>
          <a-button type="primary" @click="$emit('close')">OK</a-button>
        </div>
      </div>
    </template>
    <div v-if="data" class="info-modal">
      <div v-if="langOptions.length" class="info-modal__actions">
        <a-select
          :value="selectedLanguage"
          size="small"
          @change="handleLangChange"
          class="lang-select"
        >
          <a-select-option v-for="lang in langOptions" :key="lang.value">
            {{ lang.name }}
          </a-select-option>
        </a-select>
      </div>
      <div class="info-modal__content">
        <div class="info-modal__image">
          <img v-if="currentPreviewImage" :src="currentPreviewImage" alt="Background image" />
          <div v-else class="info-modal__no-image">
            <img src="/resources/images/empty/no-image.svg" alt="No image" />
            <span>No Image</span>
          </div>
        </div>
        <ul class="info-modal__props">
          <li class="info-modal__prop">
            <div class="info-modal__prop--label">Name for internal usage</div>
            <div class="info-modal__prop--value">{{ data.name }}</div>
          </li>
          <li class="info-modal__prop">
            <div class="info-modal__prop--label">Activity</div>
            <div class="info-modal__prop--value">{{ `${formatDate(data.period.starts_at)} – ${formatDate(data.period.ends_at)}` }}  </div>
          </li>
          <li class="info-modal__prop">
            <div class="info-modal__prop--label">Link</div>
            <div class="info-modal__prop--value">{{ `olimpbet://customPage?id=${data.uuid}` }}</div>
          </li>
          <li v-if="data.applications" class="info-modal__prop info-modal__prop--wide" style="grid-row: 3;">
            <div class="info-modal__prop--label">Apps</div>
            <div class="info-modal__prop--value">
              <a-tag class="info-modal--tag"
                v-for="app in data.applications" :key="app.id">
                {{ app.name }}
                <a-icon v-if="app.platform.toLowerCase() === 'android'" type="android" class="ml-1 text-success" />
                <a-icon v-if="app.platform.toLowerCase() === 'ios'" type="apple" class="ml-1 text-primary" />
                <a-icon v-if="app.platform.toLowerCase() === 'web'" type="global" class="ml-1 text-default" />
              </a-tag>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </a-modal>
</template>
<script>
import { mapState } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
    },
    hasEditPermission: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedLanguage: 'ru',
    }
  },
  computed: {
    ...mapState({
      languages: (state) => state.langs.langData,
    }),
    langOptions() {
      if (!this.data) {
        return
      }

      const langIds = this.data.langs.map((l) => l.lid)
      return this.languages
        .filter((language) => langIds.includes(language.id))
        .map((language) => ({
          name: language.desc,
          value: language.name,
        }))
    },
    selectedLanguageId() {
      return this.languages.find((l) => l.name === this.selectedLanguage)?.id
    },
    currentPreviewImage() {
      return this.data.header?.langs?.find(lang => lang.lid === this.selectedLanguageId)?.banner_background_path ?? null
    },
  },
  methods: {
    handleLangChange(selectedLangKey) {
      this.selectedLanguage = selectedLangKey
    },
    formatDate: function (date) {
      return this.$moment(date).format('DD.MM.YYYY')
    },
    clipboardHandle(text, info) {
      this.$clipboard(info)
      this.$notification.success({
        message: `${text} successfully copied to clipboard`,
        description: info.slice(0, 50) + '...',
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.info-modal {
  &__title {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #141322;
  }

  &__uuid {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #8c8c8c;
    margin-top: 4px;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 24px;
  }

  &__content {
    display: flex;
    gap: 16px;
  }

  &__image {
    flex: 0 0 auto;

    img {
      max-width: 250px;
      aspect-ratio: 580 / 387;
      object-fit: cover;
    }
  }

  &__no-image {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    width: 250px;
    aspect-ratio: 580 / 387;
    background-color: #fafafa;

    span {
      text-align: center;
      font-size: 12px;
      line-height: 21px;
      color: #bfbfbf;
    }
  }

  &__props {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    row-gap: 12px;
    column-gap: 8px;
    flex-wrap: wrap;
    list-style: none;
    width: 100%;
    padding: 0;
    margin: 0;
  }

  &__prop {
    flex: 0 0 50%;
    display: inline-flex;
    flex-direction: column;
    font-size: 14px;
    line-height: 22px;

    &--label {
      color: #bfbfbf;
    }

    &--value {
      color: #262626;
    }
  }

  &__prop--wide {
    flex: 0 0 100%;
  }
}
</style>
